/**
 * The signature mark: someone's whole span in the community, one tick per month.
 *
 * Counts are square-rooted before scaling. One 500-message month would
 * otherwise flatten the other ninety-five into a flat line, and the shape of a
 * person's involvement over eight years — the ramp, the plateau, the fade — is
 * the entire point of drawing it.
 *
 * Silent months are drawn as stubs rather than skipped: a two-year gap is
 * information, and a chart that closes the gap tells a comfortable lie.
 */

interface Props {
  activity: Record<string, number>;
  /** Ticks are brass on a member's own card, muted elsewhere. */
  accent?: boolean;
}

function fillMonths(activity: Record<string, number>) {
  const months = Object.keys(activity).sort();
  if (!months.length) return [];
  const [startY, startM] = months[0].split("-").map(Number);
  const [endY, endM] = months[months.length - 1].split("-").map(Number);
  const out: { key: string; value: number }[] = [];
  for (let y = startY, m = startM; y < endY || (y === endY && m <= endM); ) {
    const key = `${y}-${String(m).padStart(2, "0")}`;
    out.push({ key, value: activity[key] ?? 0 });
    if (++m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export function Strata({ activity, accent = true }: Props) {
  const series = fillMonths(activity);
  if (!series.length) return null;

  const peak = Math.max(...series.map((s) => s.value), 1);
  const peakMonth = series.find((s) => s.value === peak);
  const W = 328;
  const H = 62;
  const gap = 1;
  const w = Math.max(1.1, (W - gap * (series.length - 1)) / series.length);
  const colour = accent ? "var(--brass)" : "var(--teal)";

  const years = Array.from(new Set(series.map((s) => s.key.slice(0, 4))));

  return (
    <div className="strata">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Активность по месяцам с ${series[0].key} по ${series[series.length - 1].key}, пик ${peak} сообщений`}
      >
        {/* Baseline: the floor the ticks stand on, so silent stretches read as
            silence rather than as missing data. */}
        <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="var(--line)" strokeWidth="1" />
        {series.map((s, i) => {
          const h = s.value === 0 ? 1.5 : Math.max(2, (Math.sqrt(s.value) / Math.sqrt(peak)) * (H - 4));
          return (
            <rect
              key={s.key}
              x={i * (w + gap)}
              y={H - h}
              width={w}
              height={h}
              fill={s.value === 0 ? "var(--line-strong)" : colour}
              opacity={s.value === 0 ? 0.5 : 0.45 + 0.55 * (Math.sqrt(s.value) / Math.sqrt(peak))}
            >
              <title>{`${s.key}: ${s.value}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="strata-scale">
        <span>{years[0]}</span>
        {peakMonth && (
          <span className="strata-peak">
            пик {peak} · {peakMonth.key}
          </span>
        )}
        <span>{years[years.length - 1]}</span>
      </div>
    </div>
  );
}

/**
 * The same vocabulary, small: five ticks with `value` of `max` lit. Only ever
 * shown for a quantity the pipeline actually measured.
 */
export function Gauge({ value, max = 5, label }: { value: number; max?: number; label?: string }) {
  const lit = Math.max(0, Math.min(max, Math.round(value)));
  return (
    <span className="gauge" role="img" aria-label={label ?? `${lit} из ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <i key={i} className={i < lit ? "on" : undefined} />
      ))}
    </span>
  );
}
